import React, { useRef, useState, useEffect, useCallback } from 'react';
import * as faceapi from 'face-api.js';
import api from '../services/api';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, CircularProgress, Alert,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

const MODEL_URL = '/models';

function PessoaFotoCapture({ open, onClose, pessoaId, empresaId, onFotoSalva }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [status, setStatus] = useState('loading');
  const [erro, setErro] = useState('');
  const [capturando, setCapturando] = useState(false);
  const [fotosSalvas, setFotosSalvas] = useState(0);

  const loadModels = useCallback(async () => {
    try {
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      ]);
      setStatus('ready');
    } catch (err) {
      console.error('Erro ao carregar modelos:', err);
      setErro('Erro ao carregar modelos de reconhecimento.');
      setStatus('error');
    }
  }, []);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error('Erro ao acessar câmera:', err);
      setErro('Não foi possível acessar a câmera.');
      setStatus('error');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (open) {
      setFotosSalvas(0);
      setErro('');
      setStatus('loading');
      loadModels();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [open, loadModels, stopCamera]);

  useEffect(() => {
    if (status !== 'ready') return;
    let cancelled = false;
    const init = async () => {
      await new Promise((r) => setTimeout(r, 100));
      if (!cancelled) await startCamera();
    };
    init();
    return () => { cancelled = true; };
  }, [status, startCamera]);

  const handleCapturar = async () => {
    if (!videoRef.current || capturando) return;
    setCapturando(true);
    setErro('');

    try {
      const detection = await faceapi
        .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 }))
        .withFaceLandmarks(true)
        .withFaceDescriptor();

      if (!detection) {
        setErro('Nenhum rosto detectado. Posicione-se em frente à câmera com boa iluminação.');
        return;
      }

      const canvas = canvasRef.current;
      if (canvas) {
        const dims = faceapi.matchDimensions(canvas, videoRef.current, true);
        const resized = faceapi.resizeResults(detection, dims);
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        faceapi.draw.drawDetections(canvas, resized);
      }

      const descriptors = Array.from(detection.descriptor);
      const foto_base64 = canvas ? canvas.toDataURL('image/jpeg', 0.8) : null;

      const response = await api.post('/fotos-pessoa', {
        pessoa_id: pessoaId,
        empresa_id: empresaId,
        descriptors,
        foto_base64,
      });

      console.log('Foto salva:', response.data);
      setFotosSalvas((f) => f + 1);
      setErro('');
    } catch (err) {
      const msg = err.response?.data?.message || 'Erro ao salvar foto.';
      console.error('Erro ao capturar/salvar foto:', err);
      setErro(msg);
    } finally {
      setCapturando(false);
    }
  };

  const handleFechar = () => {
    stopCamera();
    onClose();
    if (fotosSalvas > 0 && onFotoSalva) onFotoSalva();
  };

  return (
    <Dialog open={open} onClose={handleFechar} maxWidth="sm" fullWidth>
      <DialogTitle>Capturar foto para reconhecimento</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Posicione o rosto em frente à câmera com boa iluminação. Tire múltiplas fotos em
          diferentes ângulos para melhorar o reconhecimento.
        </Typography>

        {status === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {status === 'error' && (
          <Alert severity="error">{erro}</Alert>
        )}

        {status === 'ready' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: '4/3',
                bgcolor: 'black',
                borderRadius: 2,
                overflow: 'hidden',
                mb: 2,
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transform: 'scaleX(-1)',
                }}
              />
              <canvas
                ref={canvasRef}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  transform: 'scaleX(-1)',
                }}
              />
            </Box>

            {erro && (
              <Alert severity="warning" sx={{ width: '100%', mb: 1 }}>
                {erro}
              </Alert>
            )}

            {fotosSalvas > 0 && (
              <Typography color="success.main" sx={{ mb: 1 }}>
                {fotosSalvas} foto(s) salva(s) com sucesso.
              </Typography>
            )}

            <Button
              variant="contained"
              onClick={handleCapturar}
              disabled={capturando}
              startIcon={capturando ? <CircularProgress size={20} /> : <CameraAltIcon />}
              size="large"
              fullWidth
            >
              {capturando ? 'Processando...' : 'Capturar foto'}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleFechar}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

export default PessoaFotoCapture;
